<template>
    <div class="agenda-calendar">
        <div class="agenda-calendar-grid">
            <template v-for="(row, rowIdx) in calendarRows" :key="rowIdx">
                <div class="agenda-week-row" :style="{'--multiday-count': row.multiDayBars.length}">
                    <div class="agenda-multiday-layer" v-if="row.multiDayBars.length > 0">
                        <div v-for="bar in row.multiDayBars" :key="bar.id + '-' + rowIdx"
                             class="agenda-multiday-bar"
                             :class="[bar.posClass]"
                             :style="{
                                 left: (bar.startCol / 7 * 100) + '%',
                                 width: (bar.span / 7 * 100) + '%',
                                 top: (bar.lane * 18) + 'px',
                                 backgroundColor: bar.color || '#007AFF',
                                 color: getTextColor(bar.color || '#007AFF'),
                             }"
                             :data-evt-id="bar.event.id"
                             :draggable="bar.event.user?.id === currentUserId"
                             @dragstart.stop="onBarDragStart($event, bar.event)"
                             @click.stop="$emit('eventClick', bar.event)"
                             @contextmenu.prevent.stop="$emit('eventContextMenu', $event, bar.event)"
                             @mouseenter="$emit('eventHover', {evt: bar.event, event: $event})"
                             @mouseleave="$emit('eventLeave')">
                            <template v-if="bar.showTitle">
                                <span v-if="bar.event.user?.id === currentUserId" class="agenda-multiday-bar-radio" :class="{'agenda-multiday-bar-radio--done': bar.event.status?.id === 2}" @click.stop="$emit('statusToggle', {id: bar.event.id, status_id: bar.event.status?.id === 2 ? 1 : 2})"></span>
                                <svg v-if="bar.event.recurrence" class="agenda-multiday-bar-recurrence" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                                <span class="agenda-multiday-bar-title">{{ bar.event.title }}</span>
                            </template>
                        </div>
                    </div>
                    <AgendaDayCell
                        v-for="day in row.days"
                        :key="day.date"
                        :date="day.date"
                        :events="day.events"
                        :is-today="day.isToday"
                        :is-current-month="day.isCurrentMonth"
                        :is-selected="day.date === selectedDate"
                        :current-user-id="currentUserId"
                        :max-visible="maxVisibleEvents"
                        :multiday-offset="day.multidayOffset"
                        @click="$emit('dayClick', $event)"
                        @dblclick="$emit('dayDblClick', $event)"
                        @eventDrop="$emit('eventDrop', $event)"
                        @eventClick="$emit('eventClick', $event)"
                        @eventContextMenu="(e, ev) => $emit('eventContextMenu', e, ev)"
                        @statusToggle="$emit('statusToggle', $event)"
                        @eventHover="$emit('eventHover', $event)"
                        @eventLeave="$emit('eventLeave')"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import AgendaDayCell from './AgendaDayCell.vue'

export default {
    name: 'AgendaCalendar',
    components: {AgendaDayCell},
    emits: ['dayClick', 'dayDblClick', 'eventDrop', 'eventClick', 'eventContextMenu', 'statusToggle', 'eventHover', 'eventLeave'],
    props: {
        year: {type: Number, required: true},
        month: {type: Number, required: true},
        items: {type: Array, default: () => []},
        selectedDate: {type: String, default: null},
        currentUserId: {type: [Number, String], default: null},
        maxVisibleEvents: {type: Number, default: 3},
    },
    computed: {
        multiDayItems() {
            return this.items.filter(item =>
                item.event_end_at && item.event_at &&
                item.event_end_at.substring(0, 10) !== item.event_at.substring(0, 10)
            )
        },
        singleDayEventsByDate() {
            const map = {}
            for (const item of this.items) {
                if (item.event_end_at && item.event_at &&
                    item.event_end_at.substring(0, 10) !== item.event_at.substring(0, 10)) continue
                const dateStr = (item.event_at || '').substring(0, 10)
                if (!dateStr) continue
                if (!map[dateStr]) map[dateStr] = []
                map[dateStr].push(item)
            }
            for (const key of Object.keys(map)) {
                map[key].sort((a, b) => (a.event_at || '').localeCompare(b.event_at || ''))
            }
            return map
        },
        todayStr() {
            const now = new Date()
            return this.formatDate(now.getFullYear(), now.getMonth() + 1, now.getDate())
        },
        calendarDays() {
            const days = []
            const firstDay = new Date(this.year, this.month - 1, 1)
            let startWeekday = firstDay.getDay()
            startWeekday = startWeekday === 0 ? 6 : startWeekday - 1

            const daysInMonth = new Date(this.year, this.month, 0).getDate()
            const prevMonth = this.month === 1 ? 12 : this.month - 1
            const prevYear = this.month === 1 ? this.year - 1 : this.year
            const daysInPrevMonth = new Date(prevYear, prevMonth, 0).getDate()

            for (let i = startWeekday - 1; i >= 0; i--) {
                const day = daysInPrevMonth - i
                const dateStr = this.formatDate(prevYear, prevMonth, day)
                days.push({
                    date: dateStr,
                    isCurrentMonth: false,
                    isToday: dateStr === this.todayStr,
                    events: this.singleDayEventsByDate[dateStr] || [],
                })
            }

            for (let d = 1; d <= daysInMonth; d++) {
                const dateStr = this.formatDate(this.year, this.month, d)
                days.push({
                    date: dateStr,
                    isCurrentMonth: true,
                    isToday: dateStr === this.todayStr,
                    events: this.singleDayEventsByDate[dateStr] || [],
                })
            }

            const totalRows = Math.ceil(days.length / 7)
            const totalCells = totalRows * 7
            const remaining = totalCells - days.length
            const nextMonth = this.month === 12 ? 1 : this.month + 1
            const nextYear = this.month === 12 ? this.year + 1 : this.year
            for (let d = 1; d <= remaining; d++) {
                const dateStr = this.formatDate(nextYear, nextMonth, d)
                days.push({
                    date: dateStr,
                    isCurrentMonth: false,
                    isToday: dateStr === this.todayStr,
                    events: this.singleDayEventsByDate[dateStr] || [],
                })
            }

            return days
        },
        calendarRows() {
            const rows = []
            const days = this.calendarDays
            for (let i = 0; i < days.length; i += 7) {
                const rowDays = days.slice(i, i + 7)
                const rowStart = rowDays[0].date
                const rowEnd = rowDays[6].date
                const bars = this._computeMultiDayBars(rowDays, rowStart, rowEnd)
                for (let col = 0; col < rowDays.length; col++) {
                    let maxLane = -1
                    for (const bar of bars) {
                        if (col >= bar.startCol && col < bar.startCol + bar.span)
                            maxLane = Math.max(maxLane, bar.lane)
                    }
                    rowDays[col].multidayOffset = maxLane >= 0 ? maxLane + 1 : 0
                }
                rows.push({days: rowDays, multiDayBars: bars})
            }
            return rows
        },
        rowCount() {
            return this.calendarRows.length
        },
    },
    methods: {
        formatDate(year, month, day) {
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        },
        getTextColor(bgColor) {
            if (!bgColor) return '#fff'
            const hex = bgColor.replace('#', '')
            const r = parseInt(hex.substring(0, 2), 16)
            const g = parseInt(hex.substring(2, 4), 16)
            const b = parseInt(hex.substring(4, 6), 16)
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
            return luminance > 0.6 ? '#1c1c1e' : '#ffffff'
        },
        _computeMultiDayBars(rowDays, rowStart, rowEnd) {
            const bars = []
            const dateMap = {}
            rowDays.forEach((d, idx) => { dateMap[d.date] = idx })

            for (const evt of this.multiDayItems) {
                const evtStart = evt.event_at.substring(0, 10)
                const evtEnd = evt.event_end_at.substring(0, 10)
                if (evtEnd < rowStart || evtStart > rowEnd) continue

                const startCol = evtStart < rowStart ? 0 : (dateMap[evtStart] ?? 0)
                const endCol = evtEnd > rowEnd ? 6 : (dateMap[evtEnd] ?? 6)
                const span = endCol - startCol + 1
                const isStart = evtStart >= rowStart
                const isEnd = evtEnd <= rowEnd
                let posClass = 'agenda-multiday-bar--single'
                if (isStart && !isEnd) posClass = 'agenda-multiday-bar--start'
                else if (!isStart && isEnd) posClass = 'agenda-multiday-bar--end'
                else if (!isStart && !isEnd) posClass = 'agenda-multiday-bar--mid'

                bars.push({
                    id: evt.id, event: evt, startCol, span, posClass,
                    showTitle: isStart || startCol === 0, color: evt.color, lane: 0,
                    _continuation: !isStart,
                })
            }

            // Devam eden bar'lar (önceki haftadan gelen) önce, sonra başlangıç sütunu + uzunluk
            bars.sort((a, b) => {
                if (a._continuation !== b._continuation) return a._continuation ? -1 : 1
                return a.startCol - b.startCol || b.span - a.span
            })

            const lanes = []
            for (const bar of bars) {
                let lane = 0
                let placed = false
                while (!placed) {
                    if (!lanes[lane]) lanes[lane] = []
                    const conflict = lanes[lane].some(b => !(bar.startCol >= b.startCol + b.span || bar.startCol + bar.span <= b.startCol))
                    if (!conflict) {
                        lanes[lane].push(bar)
                        bar.lane = lane
                        placed = true
                    } else {
                        lane++
                    }
                }
            }
            return bars
        },
        onBarDragStart(e, evt) {
            e.dataTransfer.setData('text/agenda-event-id', String(evt.id))
            e.dataTransfer.effectAllowed = 'move'
            const ghost = document.createElement('div')
            ghost.textContent = evt.title
            ghost.style.cssText = `position:fixed; left:-9999px; top:-9999px; padding:3px 8px; background:${evt.color || '#007AFF'}; color:${this.getTextColor(evt.color || '#007AFF')}; border-radius:4px; font-size:11px; font-weight:500; max-width:150px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;`
            document.body.appendChild(ghost)
            e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight + 10)
            requestAnimationFrame(() => ghost.remove())
        },
    },
}
</script>
